import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAppStore } from './store/useAppStore'
import { Landing } from './pages/Landing'
import { Sidebar } from './components/layout/Sidebar'
import { Dashboard } from './pages/Dashboard'
import { Services } from './pages/Services'
import { Marketing } from './pages/Marketing'
import { AIAssistant } from './pages/AIAssistant'
import { CRM } from './pages/CRM'
import { VulnerabilityAssessment } from './pages/VulnerabilityAssessment'
import { GoogleMaps } from './pages/GoogleMaps'
import { EnterpriseIntelligence } from './pages/EnterpriseIntelligence'
import { WebDesignPage } from './pages/WebDesignPage'
import { BrandingPage } from './pages/BrandingPage'
import { MobileAppPage } from './pages/MobileAppPage'
import { Pricing } from './pages/Pricing'
import { ContactUs } from './pages/ContactUs'
import { TermsOfService } from './pages/TermsOfService'
import { PrivacyPolicy } from './pages/PrivacyPolicy'
import { AboutUs } from './pages/AboutUs'
import { supabase } from './lib/supabase'

function FractalBackground() {
  return (
    <div className="fractal-bg">
      <div className="fractal-grid" />
      <div className="fractal-orb fractal-orb-1" />
      <div className="fractal-orb fractal-orb-2" />
      <div className="fractal-orb fractal-orb-3" />
      <div className="fractal-orb fractal-orb-4" />
      <div className="fractal-orb fractal-orb-5" />
      <div className="fractal-sprites">
        {Array.from({ length: 10 }).map((_, i) => (
          <div key={i} className="fractal-sprite" />
        ))}
      </div>
    </div>
  )
}

function App() {
  const { isAuthenticated, setUser, setLoading, activeView, activeServiceType } = useAppStore()
  const [authResolved, setAuthResolved] = useState(false)

  useEffect(() => {
    let ignore = false

    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession()
        if (ignore) return
        if (session?.user) {
          setUser({
            id: session.user.id,
            email: session.user.email || '',
            full_name: session.user.user_metadata?.full_name || null,
            avatar_url: session.user.user_metadata?.avatar_url || null,
            company_name: session.user.user_metadata?.company_name || null,
            role: 'admin',
            plan: 'starter',
            created_at: session.user.created_at,
            updated_at: new Date().toISOString(),
          })
          if (window.location.pathname === '/auth/callback' || window.location.pathname === '/login') {
            window.history.replaceState({}, '', '/')
          }
        }
      } catch (error) {
        console.error('Session check error:', error)
      } finally {
        if (!ignore) {
          setLoading(false)
          setAuthResolved(true)
        }
      }
    }
    checkSession()

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (ignore) return
        if (session?.user) {
          setUser({
            id: session.user.id,
            email: session.user.email || '',
            full_name: session.user.user_metadata?.full_name || null,
            avatar_url: session.user.user_metadata?.avatar_url || null,
            company_name: session.user.user_metadata?.company_name || null,
            role: 'admin',
            plan: 'starter',
            created_at: session.user.created_at,
            updated_at: new Date().toISOString(),
          })
          if (event === 'SIGNED_IN') {
            window.history.replaceState({}, '', '/')
          }
        } else {
          setUser(null)
        }
        setLoading(false)
        setAuthResolved(true)
      }
    )
    return () => {
      ignore = true
      subscription.unsubscribe()
    }
  }, [setUser, setLoading])

  if (!authResolved) return null

  if (!isAuthenticated) {
    return (
      <>
        <FractalBackground />
        <Landing />
      </>
    )
  }

  const renderActiveView = () => {
    if (activeServiceType) {
      switch (activeServiceType) {
        case 'marketing': return <Marketing />
        case 'ai_assistant': return <AIAssistant />
        case 'crm': return <CRM />
        case 'vulnerability_assessment': return <VulnerabilityAssessment />
        case 'google_maps': return <GoogleMaps />
        case 'web_design': return <WebDesignPage />
        case 'branding': return <BrandingPage />
        case 'mobile_app': return <MobileAppPage />
        case 'enterprise_intelligence': return <EnterpriseIntelligence />
        default: return <Services />
      }
    }
    switch (activeView) {
      case 'dashboard': return <Dashboard />
      case 'services': return <Services />
      case 'pricing': return <Pricing />
      case 'analytics': return <Dashboard />
      case 'contacts': return <ContactUs />
      case 'about': return <AboutUs />
      case 'terms': return <TermsOfService />
      case 'privacy': return <PrivacyPolicy />
      case 'settings': return <Dashboard />
      default: return <Dashboard />
    }
  }

  return (
    <div className="min-h-screen bg-metallic-50 relative">
      <FractalBackground />
      <div className="relative z-10">
        <Sidebar />
        <main className="lg:ml-[280px] p-6 transition-all duration-300">
          <AnimatePresence mode="wait">
            <motion.div key={activeServiceType || activeView} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.2 }}>
              {renderActiveView()}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </div>
  )
}

export default App
